import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, UploadCloud, FileCheck } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { SmsOptInCheckbox, TosCheckbox } from "@/components/Legal";

export function FileField({ label, hint, file, onPick, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">{label}</label>
      <label
        className={`flex items-center gap-3 border border-dashed p-4 cursor-pointer transition-colors ${
          file ? "border-emerald-500/60 bg-emerald-500/5" : "border-white/[0.15] bg-[#141416] hover:border-[#FF3B30]/60"
        }`}
      >
        {file ? <FileCheck className="w-5 h-5 text-emerald-400 shrink-0" /> : <UploadCloud className="w-5 h-5 text-zinc-500 shrink-0" />}
        <span className={`text-sm truncate ${file ? "text-white" : "text-zinc-500"}`}>
          {file ? file.name : hint}
        </span>
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
          className="hidden"
          data-testid={testid}
          onChange={(e) => onPick(e.target.files?.[0] || null)}
        />
      </label>
    </div>
  );
}

export default function DriverSignup() {
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").trim().toUpperCase();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", dot_number: "" });
  const [license, setLicense] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false); // optional SMS consent — never pre-checked (CTIA)
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!license || !insurance) {
      toast.error("Both documents are required — license and proof of insurance.");
      return;
    }
    if (!tosAccepted) {
      toast.error("Please agree to the Terms and Conditions and Privacy Policy to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("full_name", form.full_name.trim());
      fd.append("email", form.email.trim().toLowerCase());
      fd.append("phone", form.phone.trim());
      fd.append("dot_number", form.dot_number.trim());
      fd.append("tos_accepted", "true");
      if (refCode) fd.append("referral_code", refCode);
      if (smsOptIn) fd.append("sms_opt_in", "true");
      fd.append("license", license);
      fd.append("insurance", insurance);
      await api.post("/driver-signup", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Registration failed — please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 py-12" data-testid="driver-signup-page">
      <div className="w-full max-w-lg">
        <Link to="/" className="block text-center font-display text-2xl font-black uppercase tracking-tight mb-8">
          Boat<span className="text-[#FF3B30]">RV</span>Moving
        </Link>

        {done ? (
          <div className="border border-emerald-500/40 bg-[#0F0F10] p-10 text-center" data-testid="ds-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display text-3xl font-black uppercase">Application received</h1>
            <p className="text-zinc-400 text-sm mt-4 leading-relaxed">
              Your registration is <span className="text-white font-semibold">under review</span>. We emailed you a
              confirmation — once an admin approves your application, you'll get a link to set your password
              (valid 15 minutes) and start bidding.
            </p>
            <p className="text-zinc-600 text-xs mt-6 uppercase tracking-widest">
              Your documents are securely stored and will never be shared.
            </p>
            <Link to="/" className="inline-block mt-8 text-[11px] uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white transition-colors">
              ← Back to home
            </Link>
          </div>
        ) : (
          <div className="border border-white/[0.08] bg-[#0F0F10] p-8 md:p-10">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] font-bold">Driver registration</span>
            <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-2 leading-none">
              Drive for real money
            </h1>
            <p className="text-sm text-zinc-500 mt-3">
              Register in one step. We verify your DOT against the FMCSA SAFER database and a human reviews your documents.
            </p>

            <div className="mt-5 flex items-start gap-3 border border-emerald-500/30 bg-emerald-500/[0.06] p-4" data-testid="ds-security-note">
              <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-200/90 leading-relaxed">
                Your documents are securely stored and will never be shared.
              </p>
            </div>

            {refCode && (
              <div className="mt-4 flex items-start gap-3 border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-4" data-testid="ds-referral-banner">
                <CheckCircle2 className="w-5 h-5 text-[#FF3B30] shrink-0 mt-0.5" />
                <p className="text-xs text-zinc-300 leading-relaxed">
                  Referred by code <span className="font-black text-white tracking-widest">{refCode}</span> — your referrer earns a free month of Driver Pro once you're verified.
                </p>
              </div>
            )}

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="ds-form" noValidate>
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">Full name</label>
                <input name="full_name" value={form.full_name} onChange={onChange} required placeholder="Sam Hauler" data-testid="ds-name"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">Email</label>
                <input name="email" type="email" value={form.email} onChange={onChange} required placeholder="you@example.com" data-testid="ds-email"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">Phone</label>
                <input name="phone" value={form.phone} onChange={onChange} placeholder="(555) 123-4567" data-testid="ds-phone"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4" />
                <SmsOptInCheckbox checked={smsOptIn} onChange={setSmsOptIn} testid="ds-sms-optin" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">USDOT number</label>
                <input name="dot_number" inputMode="numeric" value={form.dot_number}
                  onChange={(e) => setForm((f) => ({ ...f, dot_number: e.target.value.replace(/\D/g, "").slice(0, 8) }))}
                  required placeholder="e.g. 3847592" data-testid="ds-dot"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4" />
                <p className="text-[11px] text-zinc-600 mt-1.5">Checked live against the FMCSA SAFER database.</p>
              </div>

              <FileField label="Driver's license" hint="Upload license — JPG, PNG or PDF (max 5 MB)" file={license} onPick={setLicense} testid="ds-license" />
              <FileField label="Proof of insurance" hint="Upload insurance certificate — JPG, PNG or PDF (max 5 MB)" file={insurance} onPick={setInsurance} testid="ds-insurance" />

              <TosCheckbox checked={tosAccepted} onChange={setTosAccepted} testid="ds-tos" />

              <button
                type="submit"
                disabled={submitting}
                data-testid="ds-submit"
                className="w-full bg-[#FF3B30] hover:bg-[#ff5044] disabled:opacity-60 text-white font-bold text-sm uppercase tracking-widest py-4 transition-all flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Verifying DOT & uploading…
                  </>
                ) : (
                  "Submit application"
                )}
              </button>
              <p className="text-[11px] text-zinc-600 text-center">
                Already approved? <Link to="/login" className="text-[#FF3B30] hover:text-white underline underline-offset-2">Sign in</Link>
              </p>
              <p className="text-[11px] text-zinc-600 text-center">
                Already applied? <Link to="/application-status" className="text-[#FF3B30] hover:text-white underline underline-offset-2">Check your status</Link>
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
